import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
  AccountFlagsFlowInterface,
  AccountFlagsFlowStateType,
  AccountFlagsFlowErrorType,
  AccountFlagsFlowPayload,
  OnAccountFlagsFlowStateChangedCallback,
} from '@central-products/interface-account-flags-flow';
import { ACCOUNT_EVENT_IDS } from '@central-products/interface-account-actions';
import { FLAGS_CONFIG } from './constants';
import { AccountFlagsContainer } from './AccountFlagsContainer';
import { AccountFlagsFlowConfig, AccountFlagsFlowDependencies, OnActionRequested } from './types';
import './index.scss';

export class AccountFlagsFlow extends AccountFlagsFlowInterface {
  private readonly config: AccountFlagsFlowConfig;
  private readonly dependencies: AccountFlagsFlowDependencies;
  private state: AccountFlagsFlowStateType = AccountFlagsFlowStateType.NONE;
  private onStateChangedCallback?: OnAccountFlagsFlowStateChangedCallback;
  private domNode: HTMLElement | null = null;

  constructor(config: AccountFlagsFlowConfig, dependencies: AccountFlagsFlowDependencies) {
    super();

    this.config = config;
    this.dependencies = dependencies;
  }

  public async init() {
    // TODO: cancel displaying the soft flag after logout which can happen in the background
  }

  public async start(payload: AccountFlagsFlowPayload) {
    if (this.state !== AccountFlagsFlowStateType.NONE) {
      throw { error: AccountFlagsFlowErrorType.INVALID_STATE };
    }

    const isFlagSupported = FLAGS_CONFIG[this.config.jurisdiction].hasOwnProperty(payload.flag);

    if (!isFlagSupported) {
      throw { error: AccountFlagsFlowErrorType.INVALID_STATE };
    }

    window.performance?.mark?.('cp:account-flags:start');
    this.render(payload);
    this.setState(AccountFlagsFlowStateType.STARTED);

    return {};
  }

  public async cancel() {
    if (this.state === AccountFlagsFlowStateType.NONE || this.domNode === null) {
      throw { error: AccountFlagsFlowErrorType.INVALID_STATE };
    }

    ReactDOM.unmountComponentAtNode(this.domNode);
    document.body.removeChild(this.domNode);
    this.domNode = null;
    this.setState(AccountFlagsFlowStateType.NONE);

    return {};
  }

  public async getState() {
    return { state: this.state };
  }

  public onStateChanged(callback: OnAccountFlagsFlowStateChangedCallback) {
    this.onStateChangedCallback = callback;
  }

  private render(payload: AccountFlagsFlowPayload) {
    this.domNode = document.createElement('div');
    document.body.appendChild(this.domNode);

    const flagConfig = FLAGS_CONFIG[this.config.jurisdiction][payload.flag];
    const onClose = () => {
      this.dependencies['account-actions'].execute({
        eventId: ACCOUNT_EVENT_IDS.ACCOUNT_FLAG_CLOSE_REQUESTED,
        context: {
          data: {
            flag: payload.flag,
            type: flagConfig.type,
          },
          userFlow: 'account-flags',
        },
      });
      this.cancel();
    };
    const onSuccess = () => {
      this.dependencies['account-actions'].execute({
        eventId: ACCOUNT_EVENT_IDS.ACCOUNT_FLAG_SUCCESS_REQUESTED,
        context: {
          data: {
            flag: payload.flag,
            type: flagConfig.type,
          },
          userFlow: 'account-flags',
        },
      });
      this.cancel();
    };
    const onQuit = () => {
      this.dependencies['account-actions'].execute({
        eventId: ACCOUNT_EVENT_IDS.ACCOUNT_FLAG_QUIT_REQUESTED,
        context: {
          data: {
            flag: payload.flag,
            type: flagConfig.type,
          },
          userFlow: 'account-flags',
        },
      });
      this.cancel();
    };
    const onLoginRequested = () => {
      this.dependencies['account-actions'].execute({
        eventId: ACCOUNT_EVENT_IDS.LOGIN_REQUESTED,
        context: { userFlow: 'account-flags' },
      });
      this.cancel();
    };
    const onActionRequested: OnActionRequested = (eventId, context) => {
      this.dependencies['account-actions'].execute({
        eventId,
        context: { ...context, userFlow: 'account-flags' },
      });
      this.cancel();
    };
    const executeLogin = async (token?: string): Promise<void> => {
      if (!token) {
        onLoginRequested();
        return;
      }

      try {
        await this.dependencies['session-manager'].tokenLogIn({ token });

        const { successEvent } = FLAGS_CONFIG[this.config.jurisdiction][payload.flag];

        if (!successEvent) {
          return;
        }

        this.dependencies['account-actions'].execute({
          eventId: successEvent,
          context: { userFlow: 'account-flags' },
        });
      } catch (error) {
        if (error?.error?.code === 'error.authentication.hard.flag') {
          this.dependencies['account-actions'].execute({
            eventId: ACCOUNT_EVENT_IDS.HARD_FLAG_REQUESTED,
            context: { data: error?.error?.data, userFlow: 'account-flags' },
          });
        } else {
          onLoginRequested();
        }
      }
    };

    ReactDOM.render(
      <AccountFlagsContainer
        environment={this.config.environment}
        jurisdiction={this.config.jurisdiction}
        locale={this.config.locale}
        dataAccessService={this.dependencies['data-access-service']}
        flag={{
          name: flagConfig.app,
          type: flagConfig.type,
          token: payload.token,
          settings: flagConfig.settings,
          options: flagConfig.options,
        }}
        onClose={onClose}
        onSuccess={onSuccess}
        onQuit={onQuit}
        onLoginRequested={onLoginRequested}
        onActionRequested={onActionRequested}
        executeLogin={executeLogin}
      />,
      this.domNode
    );
  }

  private setState(state: AccountFlagsFlowStateType) {
    if (this.state === state) {
      return;
    }

    this.state = state;

    if (this.onStateChangedCallback) {
      this.onStateChangedCallback({ state });
    }
  }
}
